/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2023] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import { View } from 'backbone.marionette';
import Backbone from 'backbone';
import _ from 'lodash';
import Ladda from 'ladda';
import Radio from 'backbone.radio';

import showModal from '@/util/showModal';
import ConfirmationModalView from '@/views/ConfirmationModalView';
import showErrorMessage from '@/util/showErrorMessage';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import { parseServerDate, formatServerDate, parseDisplayDate } from '@/util/MomentUtils';

import InventoryDashboardGridView from '../../views/InventoryDashboardGridView/InventoryDashboardGridView';
import AssetFormCollectionView from '../../views/AssetFormCollectionView/AssetFormCollectionView';
import CreateAssetChangeRequest from '../../services/CreateAssetChangeRequest';

import tmplAssetChangeRequestPage from './AssetChangeRequestPage.hbs';

const AssetChangeRequestPage = View.extend({
  template: tmplAssetChangeRequestPage,

  ui: {
    assetsGrid: '#assetsGrid',
    assetForm: '#assetForm',
    submitBtn: '.btn-submit-change-request'
  },

  regions: {
    assetsGrid: '@ui.assetsGrid',
    assetForm: '@ui.assetForm'
  },

  events: {
    'click @ui.submitBtn': 'submitAssetChangeRequest'
  },

  childViewEvents: {
    'grid:checkbox:changed': 'showAssetFormCollection',
    'update:asset': 'updateAssetInGrid'
  },


  templateContext() {
    return {
        assets: this.model.get('assets')
    };
  },

  onRender() {
    const assets = this.model.get('assets');
    this.assetsGrid = new InventoryDashboardGridView({
      isAssetChangeRequest: true
    });
    this.assetsGrid.showSelectColumn();
    this.showChildView('assetsGrid', this.assetsGrid);
    this.assetsGrid.collection.fullCollection.reset(assets);
  },

  async showAssetFormCollection(assets) {
    if(!assets) {
      assets = this.assetsGrid.viewGrid.getSelectedModels();
    }

    this.selectedModels = []

    _.each(assets, (asset) => {
      asset.set('rowIndex', this.assetsGrid.collection.fullCollection.indexOf(asset))
      this.selectedModels.push(asset)
    })

    this.showChildView('assetForm', new AssetFormCollectionView({
      collection: new Backbone.Collection(assets),
      model: new Backbone.Model({})
    }));
  },

  async updateAssetInGrid(asset) {
    const oldRow = await this.getMatchingRow(this.assetsGrid.viewGrid.getSelectedModels(), asset);
    this.assetsGrid.collection.fullCollection.remove(oldRow);

    asset.contractAwardDate = formatServerDate(parseDisplayDate(asset.contractAwardDate));
    asset.contractEndDate = formatServerDate(parseDisplayDate(asset.contractEndDate));
    asset.eqInstallDate = formatServerDate(parseDisplayDate(asset.eqInstallDate));
    asset.uninstallDate = formatServerDate(parseDisplayDate(asset.uninstallDate));
    this.assetsGrid.collection.fullCollection.add(asset);

    const newRow = await this.getMatchingRow(this.assetsGrid.collection.fullCollection.models, asset);
    if(newRow.get('asset')) { //if the asset number is an empty string, skip checking coordinating grid row to avoid errors and allow form validation.
      const rowSelector = `.${newRow.get('asset')}`;
      this.ui.assetsGrid.find(rowSelector).prop('checked', true).change();
    }
  },

  async getMatchingRow(models, asset) {
    const model = _.find(models, (row) => {
      return row.get('rowIndex') === asset.rowIndex
    })
    return model;
  },

  submitAssetChangeRequest(e) {
    e.preventDefault();

    const self = this;
    let assetsString = '';
    const gridCollection = [];

    _.each(this.assetsGrid.collection.fullCollection.models, (row, i) => {
      assetsString = i < (this.assetsGrid.collection.fullCollection.models.length - 1) 
        ? assetsString.concat(row.get('asset'), ', ')
        : assetsString.concat('and ', row.get('asset'));
      gridCollection.push(row.attributes);

      row.attributes.contractAwardDate = formatServerDate(parseServerDate(row.attributes.contractAwardDate))
      row.attributes.contractEndDate = formatServerDate(parseServerDate(row.attributes.contractEndDate))
      row.attributes.eqInstallDate = formatServerDate(parseServerDate(row.attributes.eqInstallDate))
      row.attributes.uninstallDate = formatServerDate(parseServerDate(row.attributes.uninstallDate))
    })

    showModal({
      title: 'Submit Asset Change Request',
      view: new ConfirmationModalView({
        confirmText: 'Yes',
        model: new Backbone.Model({
          message: `Are you sure you would like to submit a change request for the following assets: ${assetsString}? Your asset change request will be sent for approval to an administrator. If the request is approved, the changes will be made in EBS. The inventory in EMS will be updated the next time the AMR is processed.`,
          async onConfirm() {
            const spinner = Ladda.create(self.ui.submitBtn[0]);
            spinner.start();
            try {
              await Promise.all([CreateAssetChangeRequest(gridCollection)]);
              self.showCreateSuccessMessage();
            } catch(err) {
              self.showCreateAssetChangeRequestError(err);
            } finally {
              self.render();
              spinner.stop();
              Radio.channel('navigate').request('show:int:inventory');
            }
          }
        })
      })
    });
  },

  showCreateAssetChangeRequestError(err) {
    showErrorMessage({
      errorCode: 'createACRError',
      errorTitle: 'Error Creating Asset Change Request',
      errorMessage: 'An error occurred while attempting to create this asset change request. Please try again or contact an administrator.'
    });
  },

  showCreateSuccessMessage() {
    showSuccessMessage({
      code: 'createACRSuccess',
      title: 'Asset Change Request Submitted',
      message: 'This asset change request has been submitted successfully.',
      hideAfter: 5
    });
  }
});

export default AssetChangeRequestPage;
